/**
 * braze-form-events.js
 *
 * Braze identification and event tracking for intimaterose.com.
 *
 * Identifies users in Braze via sha256(email) as external_id when:
 *   1. A Braze email/SMS link arrives with ?email= in the URL
 *   2. A visitor submits the footer newsletter form
 *   3. A visitor submits the Alia popup
 *   4. A logged-in Shopify customer visits the site (and isn't already identified)
 *
 * Depends on: window.irBrazeStorage (ir-braze-storage.js, loaded before this)
 *
 * See PR_137_BRAZE_REVIEW.md for full architecture rationale.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Email utilities
  // ---------------------------------------------------------------------------

  function normalizeEmail(raw) {
    if (raw == null) return '';
    var s = String(raw).trim().toLowerCase();
    if (!s) return '';
    return s;
  }

  function isValidEmail(email) {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Top 100 disposable email domains — curated from the disposable-email-domains
  // npm package (4,727 domains). This subset covers the most commonly abused
  // services and their TLD variants. PR #137 used the full npm list server-side;
  // we use a curated top-100 client-side to avoid adding ~80KB to the page.
  // To update: https://github.com/disposable-email-domains/disposable-email-domains
  var DISPOSABLE_DOMAINS = [
    // Mailinator network
    'mailinator.com', 'mailinator.net', 'mailinator.org', 'mailinator.us',
    // Guerrillamail network
    'guerrillamail.com', 'guerrillamail.de', 'guerrillamail.net', 'guerrillamail.org',
    'guerrillamail.biz', 'guerrillamail.info', 'guerrillamailblock.com', 'grr.la',
    // 10-minute mail variants
    '10minutemail.com', '10minutemail.de', '10minutemail.net', '10minutemail.us',
    '10minutemail.pro', '10minutemail.cf', '10minutemail.ga', '10minutemail.gq',
    '10minutemail.ml', '10minutemail.nl', '10minutemail.co.uk', '10minutemail.co.za',
    '10minutemail.be', '10minutemail.tk', '10minutemail.xyz',
    // Temp mail variants
    'tempmail.com', 'tempmail.de', 'tempmail.io', 'tempmail.net', 'tempmail.email',
    'tempmail.plus', 'tempmail.us', 'tempmail.ninja',
    'temp-mail.org', 'temp-mail.com', 'temp-mail.ru', 'temp-mail.de',
    'tmpmail.org', 'tmpmail.net',
    // Yopmail
    'yopmail.com', 'yopmail.fr', 'yopmail.gq', 'yopmail.net',
    // Trashmail network
    'trashmail.com', 'trashmail.de', 'trashmail.io', 'trashmail.net',
    'trashmail.ws', 'trashmail.at', 'trash-mail.com', 'trash-mail.de',
    // Sharklasers (guerrillamail related)
    'sharklasers.com',
    // Maildrop
    'maildrop.cc', 'maildrop.cf', 'maildrop.ga', 'maildrop.gq', 'maildrop.ml',
    // 1secmail
    '1secmail.com', '1secmail.net', '1secmail.org',
    // Throwaway
    'throwaway.email', 'throwawaymail.com',
    // GetNada
    'getnada.com', 'getnada.cc',
    // GetAirmail
    'getairmail.com', 'getairmail.cf', 'getairmail.ga', 'getairmail.gq',
    'getairmail.ml', 'getairmail.tk',
    // FakeInbox
    'fakeinbox.com', 'fakeinbox.cf', 'fakeinbox.ga', 'fakeinbox.ml', 'fakeinbox.tk',
    // Mohmal
    'mohmal.com', 'mohmal.im', 'mohmal.in', 'mohmal.tech',
    // Email-fake
    'email-fake.com', 'email-fake.ml', 'email-fake.cf', 'email-fake.ga', 'email-fake.tk',
    // Individual popular services
    'dispostable.com', 'mailnesia.com', 'tempail.com', 'spam4.me',
    'burnermail.com', 'mailsac.com', 'mintemail.com', 'mytemp.email',
    'easytrashmail.com', 'mail-temporaire.fr', 'tempinbox.com', 'mvrht.net',
    'mailcatch.com', 'inboxbear.com', 'tempmailaddress.com'
  ];

  function isDisposableEmail(email) {
    var at = email.lastIndexOf('@');
    if (at < 1) return false;
    var domain = email.slice(at + 1);
    for (var i = 0; i < DISPOSABLE_DOMAINS.length; i++) {
      if (DISPOSABLE_DOMAINS[i] === domain) return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // SHA-256 via WebCrypto — used to hash email into a non-PII external_id
  // ---------------------------------------------------------------------------

  function sha256HexBrowser(str) {
    var enc = new TextEncoder();
    return crypto.subtle.digest('SHA-256', enc.encode(str)).then(function (buf) {
      var bytes = new Uint8Array(buf);
      var hex = '';
      for (var i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, '0');
      }
      return hex;
    });
  }

  // ---------------------------------------------------------------------------
  // Debug helper
  // ---------------------------------------------------------------------------

  function isDebug() {
    if (window.__IR_BRAZE_DEBUG__) return true;
    try { return localStorage.getItem('__IR_BRAZE_DEBUG__') === '1'; } catch (_) { return false; }
  }

  // ---------------------------------------------------------------------------
  // Braze SDK accessor — waits for window.braze
  //
  // The Shopify Braze app embed loads the SDK with on_login mode. With sha256
  // as external_id, changeUser creates a fresh SDK-owned profile and the SDK
  // sends data normally — no need to destroy/reinitialize the Shopify app's
  // state. This preserves the Shopify app's auto-tracking (product views,
  // cart updates, checkout events).
  // ---------------------------------------------------------------------------

  function isSdkReady() {
    return !!window.braze && typeof window.braze.changeUser === 'function';
  }

  function ensureBrazeSdk() {
    if (isSdkReady()) return Promise.resolve(window.braze);
    return new Promise(function (resolve) {
      var attempts = 0;
      var maxAttempts = 50; // 50 * 100ms = 5s
      var iv = setInterval(function () {
        if (isSdkReady()) {
          clearInterval(iv);
          resolve(window.braze);
        } else if (++attempts >= maxAttempts) {
          clearInterval(iv);
          console.warn('[IR Braze] SDK not available after 5s');
          resolve(null);
        }
      }, 100);
    });
  }

  // ---------------------------------------------------------------------------
  // Core identification function
  //
  // NOTE: Braze identification intentionally does not check marketing consent
  // before firing. 90% of intimaterose.com traffic is US-based where this is a
  // non-issue. We honor explicit opt-out by wiping device state on the
  // visitorConsentCollected event. See PR_137_BRAZE_REVIEW.md Q19.
  // ---------------------------------------------------------------------------

  function captureLeadEmail(emailNorm, source, extraProps) {
    if (!isValidEmail(emailNorm)) return;
    if (isDisposableEmail(emailNorm)) {
      if (isDebug()) console.info('[IR Braze] disposable email blocked', emailNorm);
      return;
    }

    // Use sha256(email) as Braze external_id. This matches the Blotout/EdgeTag
    // server-side integration which also uses sha256(email). No PII in the
    // external_id or in the cookie.
    sha256HexBrowser(emailNorm).then(function (externalId) {
      // Persist BEFORE touching Braze so a refresh mid-flow keeps state
      if (window.irBrazeStorage) {
        window.irBrazeStorage.setId(externalId);
        window.irBrazeStorage.setEmail(emailNorm);
      }

      ensureBrazeSdk().then(function (sdk) {
        if (!sdk) {
          console.warn('[IR Braze] captureLeadEmail: SDK unavailable, identify deferred', {
            source: source,
            external_id: externalId
          });
          return;
        }

        // Identify with sha256(email) as external_id.
        sdk.changeUser(externalId);
        sdk.openSession();

        try {
          var user = sdk.getUser();
          if (user && typeof user.setEmail === 'function') {
            user.setEmail(emailNorm);
          }
          if (user && typeof user.setEmailNotificationSubscriptionType === 'function') {
            user.setEmailNotificationSubscriptionType('opted_in');
          }
          // Set phone as first-class attribute (enables SMS campaigns in Braze)
          if (extraProps && extraProps.phone && typeof user.setPhoneNumber === 'function') {
            user.setPhoneNumber(String(extraProps.phone));
          }
        } catch (_) {}

        // Build event properties — all form fields go here as event properties,
        // NOT as custom user attributes (avoids HIPAA/privacy concerns with
        // health data persisted on user profiles)
        var props = { source: source };
        if (extraProps && typeof extraProps === 'object') {
          var keys = Object.keys(extraProps);
          var formFields = {};
          for (var i = 0; i < keys.length; i++) {
            props[keys[i]] = extraProps[keys[i]];
            // Collect form_* and alia_* fields into a JSON blob for easy querying
            if (keys[i].indexOf('form_') === 0 || keys[i].indexOf('alia_') === 0) {
              formFields[keys[i]] = extraProps[keys[i]];
            }
          }
          // Add a single form_data JSON property with all captured fields
          if (Object.keys(formFields).length > 0) {
            props.form_data = JSON.stringify(formFields);
          }
        }
        sdk.logCustomEvent(source + '_email_capture', props);
        sdk.requestImmediateDataFlush();

        if (isDebug()) {
          console.info('[IR Braze] captureLeadEmail: identified', {
            source: source,
            external_id: externalId,
            email: emailNorm
          });
        }
      });
    }).catch(function (err) {
      console.error('[IR Braze] captureLeadEmail error', err);
    });
  }

  // ---------------------------------------------------------------------------
  // Form email field finder — 3-layer selector
  // ---------------------------------------------------------------------------

  function findEmailInputInForm(form) {
    // 1. Explicit opt-in via data attribute
    var explicit = form.querySelector('[data-braze-email-field]');
    if (explicit) return explicit;

    // 2. Type-based: input[type=email], skip honeypots/confirm fields
    var candidates = form.querySelectorAll('input[type="email"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.hidden) continue;
      if (el.getAttribute('aria-hidden') === 'true') continue;
      if (el.style && (el.style.display === 'none' || el.style.visibility === 'hidden')) continue;
      var nameAndId = ((el.name || '') + ' ' + (el.id || '')).toLowerCase();
      if (/confirm|honeypot|trap|hp_/.test(nameAndId)) continue;
      return el;
    }

    // 3. Name-based fallback
    var byName = form.querySelector(
      'input[name="contact[email]"], input[name="email"], input[name="fields[email]"]'
    );
    if (byName) return byName;

    return null;
  }

  // ---------------------------------------------------------------------------
  // Alia popup detection — prevents double-tracking on Alia's internal forms
  // ---------------------------------------------------------------------------

  function findAliaPopupRoot(form) {
    if (!form) return null;
    var el = form;
    while (el) {
      if (el.nodeType === 1 && el.id && /^alia-root-/.test(el.id)) return el;
      var parent = el.parentElement;
      if (parent) {
        el = parent;
      } else {
        var rn = el.getRootNode && el.getRootNode();
        if (rn && rn.host) {
          el = rn.host;
        } else {
          break;
        }
      }
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // Form field scraping (generic)
  //
  // Scrapes ALL form fields from any container — selects, inputs, textareas,
  // checkboxes, radios. Works for Alia popups, Drip forms, and any future forms.
  // Fields are sent as event properties (not custom user attributes).
  //
  // For Alia specifically: the popup may unmount before alia:signup fires, so
  // we stash on every click to capture values while the popup is still in the DOM.
  // For Drip/regular forms: we scrape directly at submit time (form is still in DOM).
  // ---------------------------------------------------------------------------

  var aliaFormDataStash = {};

  function cleanFieldName(rawName) {
    // Strip UUID suffixes: single_answer_23b3a6ee-2569-... -> single_answer
    var name = String(rawName || '')
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
    // Clean for Braze property naming — lowercase, underscores only
    name = name.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_+|_+$/g, '').replace(/_+/g, '_');
    return name || 'unknown_field';
  }

  function stashAliaFormData() {
    // Early return when no Alia popup is present (most page views)
    if (!document.querySelector('[id^="alia-root-"]')) return;
    var roots = document.querySelectorAll('[id^="alia-root-"]');
    for (var r = 0; r < roots.length; r++) {
      var elements = roots[r].querySelectorAll('input, select, textarea');
      for (var i = 0; i < elements.length; i++) {
        var el = elements[i];
        if (el.type === 'hidden') continue;
        var rawName = el.getAttribute('name') || el.id || '';
        if (!rawName) continue;
        // Skip email/phone — those come through the alia:signup event detail
        if (/email|phone/i.test(rawName)) continue;

        var value = '';
        if (el.tagName === 'SELECT') {
          if (el.selectedIndex > 0) value = el.value; // skip placeholder at index 0
        } else if (el.type === 'checkbox' || el.type === 'radio') {
          if (el.checked) value = el.value || 'true';
        } else {
          value = el.value;
        }

        if (value && String(value).trim()) {
          // Derive a meaningful, unique field name:
          // - Selects: use placeholder option text (e.g., "Select Symptom" → "select_symptom")
          // - Text inputs/textareas: use placeholder attribute if the name is generic
          // - Fallback: use the HTML name attribute (UUID stripped)
          // This prevents collisions when multiple fields share a generic name pattern.
          var cleanName = cleanFieldName(rawName);
          if (el.tagName === 'SELECT' && el.options.length > 0) {
            var firstOpt = el.options[0];
            if (firstOpt && (firstOpt.value === '' || firstOpt.disabled) && firstOpt.text) {
              cleanName = cleanFieldName(firstOpt.text);
            }
          } else if ((el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && el.placeholder) {
            var placeholderName = cleanFieldName(el.placeholder);
            if (placeholderName && placeholderName !== 'unknown_field') {
              cleanName = placeholderName;
            }
          }
          aliaFormDataStash[cleanName] = String(value).trim().slice(0, 512);
        }
      }
    }
  }

  function getAndClearAliaFormData() {
    // One final scrape (popup might still be in DOM)
    stashAliaFormData();
    var data = {};
    var keys = Object.keys(aliaFormDataStash);
    for (var i = 0; i < keys.length; i++) {
      data[keys[i]] = aliaFormDataStash[keys[i]];
    }
    aliaFormDataStash = {}; // clear after reading — don't carry to next popup
    return data;
  }

  // Scrape all fields from a form element directly (for Drip forms and other
  // forms where the form is still in the DOM at submit time)
  function scrapeFormFields(form) {
    var data = {};
    var elements = form.querySelectorAll('input, select, textarea');
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (el.type === 'hidden' && !el.value) continue;
      var rawName = el.getAttribute('name') || el.id || '';
      if (!rawName) continue;
      if (/email/i.test(rawName)) continue;

      var value = '';
      if (el.tagName === 'SELECT') {
        if (el.selectedIndex > 0) value = el.value;
      } else if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) value = el.value || 'true';
      } else {
        value = el.value;
      }

      if (value && String(value).trim()) {
        var cleanName = cleanFieldName(rawName);
        if (el.tagName === 'SELECT' && el.options.length > 0) {
          var firstOpt = el.options[0];
          if (firstOpt && (firstOpt.value === '' || firstOpt.disabled) && firstOpt.text) {
            cleanName = cleanFieldName(firstOpt.text);
          }
        } else if ((el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && el.placeholder) {
          var placeholderName = cleanFieldName(el.placeholder);
          if (placeholderName && placeholderName !== 'unknown_field') {
            cleanName = placeholderName;
          }
        }
        data[cleanName] = String(value).trim().slice(0, 512);
      }
    }
    return data;
  }

  // ---------------------------------------------------------------------------
  // Alia signup event handler
  // ---------------------------------------------------------------------------

  function aliaSignupTypeAllowed(t) {
    if (t == null || t === '') return true;
    var s = String(t).trim().toLowerCase();
    return s === 'signup' || s === 'submit' || s === 'lead' ||
      s === 'completed' || s === 'conversion' || s === 'form_submit';
  }

  function onAliaSignup(e) {
    var d = e && e.detail;
    if (!d || typeof d !== 'object') {
      if (isDebug()) console.warn('[IR Braze] alia:signup missing detail', e);
      return;
    }
    if (!aliaSignupTypeAllowed(d.type)) {
      if (isDebug()) console.warn('[IR Braze] alia:signup ignored: unsupported type', d.type);
      return;
    }

    // Extract email — Alia uses inconsistent field names
    var emailRaw =
      (d.email != null && String(d.email).trim()) ? String(d.email)
      : (d.emailAddress != null && String(d.emailAddress).trim()) ? String(d.emailAddress)
      : (d.email_address != null && String(d.email_address).trim()) ? String(d.email_address)
      : '';
    var emailNorm = normalizeEmail(emailRaw);

    if (!emailNorm) {
      // Phone-only signup — skip for Phase 1 (we key on email)
      if (isDebug()) console.info('[IR Braze] alia:signup skipped: no email (phone-only?)', d);
      return;
    }
    if (!isValidEmail(emailNorm)) {
      if (isDebug()) console.warn('[IR Braze] alia:signup ignored: invalid email', emailNorm);
      return;
    }

    var popupId = d.popupID != null ? d.popupID : d.popupId;
    if (popupId == null || popupId === '') {
      try {
        var fid = new URL(window.location.href).searchParams.get('flow_id');
        if (fid) popupId = fid;
      } catch (_) {}
    }

    // Scrape all form fields from the Alia popup (dropdowns, text, etc.)
    var aliaFields = getAndClearAliaFormData();

    var props = {
      popup_id: popupId != null ? String(popupId) : undefined,
      campaign_id: d.campaignID != null ? String(d.campaignID) : (d.campaignId != null ? String(d.campaignId) : undefined),
      campaign_title: d.campaignTitle != null ? String(d.campaignTitle) : (d.campaign_title != null ? String(d.campaign_title) : undefined)
    };

    // Merge all scraped Alia form fields into props with alia_ prefix
    var fieldKeys = Object.keys(aliaFields);
    for (var f = 0; f < fieldKeys.length; f++) {
      props['alia_' + fieldKeys[f]] = aliaFields[fieldKeys[f]];
    }

    var phone = d.phone ? String(d.phone).trim() : '';
    if (!phone && d.phoneNumber) phone = String(d.phoneNumber).trim();
    if (phone) props.phone = phone;

    if (isDebug() && fieldKeys.length) {
      console.info('[IR Braze] Alia form fields captured', aliaFields);
    }

    captureLeadEmail(emailNorm, 'popup', props);
  }

  // Dedupe wrapper — Alia may dispatch on both window and document
  var lastAliaDedupeKey = '';
  function onAliaSignupFromDom(e) {
    var d = e && e.detail;
    var part = '';
    if (d && typeof d === 'object') {
      part = String(d.email || d.emailAddress || d.email_address || '') + '|' +
        String(d.phone || d.phoneNumber || '') + '|' +
        String(d.popupID != null ? d.popupID : d.popupId != null ? d.popupId : '');
    }
    var key = String(e && e.timeStamp) + '|' + part;
    if (key === lastAliaDedupeKey) return;
    lastAliaDedupeKey = key;

    if (isDebug()) {
      console.info('[IR Braze] alia:signup received', {
        type: d && d.type,
        hasEmail: !!(d && (d.email || d.emailAddress || d.email_address)),
        hasPhone: !!(d && (d.phone || d.phoneNumber)),
        popupId: d && (d.popupID != null ? d.popupID : d.popupId)
      });
      try {
        console.info('[IR Braze] alia:signup FULL detail', JSON.parse(JSON.stringify(d)));
      } catch (_) {}
    }
    onAliaSignup(e);
  }

  // ---------------------------------------------------------------------------
  // Form submit handler — fires for any form with data-braze-track attribute
  // ---------------------------------------------------------------------------

  function showFormSuccess(form) {
    // Find the success message element near this form
    var msg = form.querySelector('[class*="newsletter-form__message--success"]');
    if (!msg) {
      // Create one dynamically (for Drip forms and other forms without a pre-built message)
      msg = document.createElement('p');
      msg.className = 'newsletter-form__message newsletter-form__message--success form__message';
      msg.setAttribute('role', 'status');
      msg.textContent = 'Thanks for subscribing!';
      form.appendChild(msg);
    }
    msg.style.display = '';
    msg.focus();
    // Hide the input field area
    var fieldWrapper = form.querySelector('.newsletter-form__field-wrapper');
    if (fieldWrapper) fieldWrapper.style.display = 'none';
  }

  function onFormSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    // Skip Alia popup forms — handled by the alia:signup listener
    if (findAliaPopupRoot(form)) return;
    // data-braze-track can be on the <form> itself (plain HTML forms)
    // or on a hidden <input> child (Shopify {% form %} tags where we can't modify the element)
    var trackName = form.getAttribute('data-braze-track');
    if (!trackName) {
      var marker = form.querySelector('[data-braze-track]');
      if (marker) trackName = marker.getAttribute('data-braze-track');
    }
    // Also intercept Drip forms site-wide — send to Braze instead of getdrip.com.
    // This catches all forms with data-drip-embedded-form (homepage, blog, etc.)
    // without needing to modify Replo-generated files.
    if (!trackName && form.hasAttribute('data-drip-embedded-form')) {
      trackName = 'onpage';
    }
    if (!trackName) return;

    var emailInput = findEmailInputInForm(form);
    if (!emailInput) {
      if (isDebug()) console.warn('[IR Braze] form has data-braze-track but no email field', form);
      return;
    }
    var emailNorm = normalizeEmail(emailInput.value);
    if (!emailNorm || !isValidEmail(emailNorm)) return;

    // Prevent default form submission (no page navigation or new tab)
    e.preventDefault();

    // Scrape all non-email fields from the form (name, zip, phone, condition, etc.)
    var formFields = scrapeFormFields(form);
    var extraProps = {
      placement: form.closest('.footer-block__newsletter') ? 'footer' : 'inline'
    };
    // Add all scraped fields with form_ prefix as event properties
    var fieldKeys = Object.keys(formFields);
    for (var f = 0; f < fieldKeys.length; f++) {
      extraProps['form_' + fieldKeys[f]] = formFields[fieldKeys[f]];
    }

    // Extract phone explicitly so captureLeadEmail can call setPhoneNumber
    var phoneInput = form.querySelector('input[type="tel"], input[name*="phone" i]');
    if (phoneInput && phoneInput.value && phoneInput.value.trim()) {
      extraProps.phone = phoneInput.value.trim();
    }

    captureLeadEmail(emailNorm, trackName, extraProps);

    // Show success message
    showFormSuccess(form);
  }

  // ---------------------------------------------------------------------------
  // Page-load identity restore
  // ---------------------------------------------------------------------------

  function runInitialBrazeRestore() {
    // Source 1: URL params from Braze email/SMS link
    // Format: ?external_id={sha256_hash}&email={email_address}
    // Use external_id directly if provided (it's the hash Braze already knows).
    // Fall back to computing sha256(email) if only email is present.
    var urlExternalId = window.__IR_BRAZE_URL_EXTERNAL_ID__;
    var urlEmail = window.__IR_BRAZE_URL_EMAIL__ ? normalizeEmail(window.__IR_BRAZE_URL_EMAIL__) : null;
    if (urlExternalId || urlEmail) {
      var identifyFromUrl = function (externalId) {
        if (window.irBrazeStorage) {
          window.irBrazeStorage.setId(externalId);
          if (urlEmail) window.irBrazeStorage.setEmail(urlEmail);
        }
        ensureBrazeSdk().then(function (sdk) {
          if (!sdk) return;
          sdk.changeUser(externalId);
          sdk.openSession();
          if (urlEmail) {
            try {
              var user = sdk.getUser();
              if (user && typeof user.setEmail === 'function') user.setEmail(urlEmail);
            } catch (_) {}
          }
          sdk.logCustomEvent('braze_email_click', { source: 'url_link' });
          sdk.requestImmediateDataFlush();
          if (isDebug()) {
            console.info('[IR Braze] URL link identified (Braze email click)', {
              external_id: externalId, email: urlEmail
            });
          }
        });
      };

      if (urlExternalId) {
        // Use the external_id from the URL directly (already the correct hash)
        identifyFromUrl(urlExternalId);
      } else if (urlEmail && isValidEmail(urlEmail)) {
        // No external_id in URL — compute sha256 from email
        sha256HexBrowser(urlEmail).then(identifyFromUrl);
      }
      return;
    }

    // Source 2: cookie/localStorage from a prior identify
    var storage = window.irBrazeStorage;
    var storedId = storage ? storage.getId() : null;
    var storedEmail = storage ? storage.getEmail() : null;
    if (storedId) {
      ensureBrazeSdk().then(function (sdk) {
        if (!sdk) return;
        sdk.changeUser(storedId);
        sdk.openSession();
        if (storedEmail) {
          try {
            var user = sdk.getUser();
            if (user && typeof user.setEmail === 'function') user.setEmail(storedEmail);
          } catch (_) {}
        }
        if (isDebug()) {
          console.info('[IR Braze] restored identity from storage', {
            external_id: storedId, has_email: !!storedEmail
          });
        }
      });
      return;
    }

    // Source 3: logged-in Shopify customer (no prior cookie/storage)
    if (window.__IR_SHOPIFY_CUSTOMER_EMAIL__) {
      captureLeadEmail(
        normalizeEmail(window.__IR_SHOPIFY_CUSTOMER_EMAIL__),
        'login',
        window.__IR_SHOPIFY_CUSTOMER_ID__
          ? { customer_id: String(window.__IR_SHOPIFY_CUSTOMER_ID__) }
          : {}
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Consent denial handler — clear identity when user explicitly denies
  // ---------------------------------------------------------------------------

  function onConsentCollected() {
    if (!window.irConsent || typeof window.irConsent.isAllowed !== 'function') return;
    if (window.irConsent.isAllowed('marketing') !== false) return;

    // User explicitly denied marketing consent — wipe Braze identity
    if (window.irBrazeStorage) window.irBrazeStorage.clear();
    if (window.braze && typeof window.braze.wipeData === 'function') {
      try { window.braze.wipeData(); } catch (_) {}
    }
    console.info('[IR Braze] identity cleared — user denied marketing consent');
  }

  // ---------------------------------------------------------------------------
  // Alia form data stashing (captures values before Alia unmounts the popup)
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Wire everything up
  // ---------------------------------------------------------------------------

  function init() {
    // Page-load restore
    runInitialBrazeRestore();

    // Form submit listener (footer newsletter, etc.)
    document.addEventListener('submit', onFormSubmit, true);

    // Alia signup listeners — both window and document, capture phase
    window.addEventListener('alia:signup', onAliaSignupFromDom, true);
    document.addEventListener('alia:signup', onAliaSignupFromDom, true);

    // Stash Alia form data on every click and change — captures dropdown
    // selections, text inputs, etc. before Alia unmounts the popup on submit
    document.addEventListener('click', stashAliaFormData, true);
    document.addEventListener('change', stashAliaFormData, true);

    // Consent denial — Shopify fires this when user clicks the consent banner
    document.addEventListener('visitorConsentCollected', onConsentCollected);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
